import React, { useState, useEffect } from 'react';
import { API_BASE } from './config';
import SupportModal from './components/SupportModal';
import AddressModal from './components/AddressModal';
import LoginPage from './components/LoginPage';
import DownloadsPage from './components/DownloadsPage';
import LogoutPage from './components/LogoutPage';
import SignaturePage from './components/SignaturePage';
import TermsPage from './components/TermsPage';
import RefundPolicyPage from './components/RefundPolicyPage';
import PrivacyPage from './components/PrivacyPage';
// import Documentation from './components/Documentation';

function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [isDocumentationOpen, setIsDocumentationOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  
  // const toggleDocumentation = () => setIsDocumentationOpen(!isDocumentationOpen);

  // Handle scroll to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check for payment success on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment') === 'success') {
      setIsModalOpen(true);
    } else if (urlParams.get('payment') === 'cancelled') {
      // Fallback: if somehow user lands on main page with cancelled param
      // Try to close the tab
      try {
        window.close();
        if (window.opener) {
          window.close();
        }
      } catch (e) {
        // If closing fails, clean up the URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const [route, setRoute] = useState(window.location.pathname);
  const [isAuthed, setIsAuthed] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [meInfo, setMeInfo] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressPrompted, setAddressPrompted] = useState(false);
  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (to) => {
    window.history.pushState({}, '', to);
    setRoute(to);
  };

  const isHome = route === '/';

  // Check authentication status whenever route changes (and on mount)
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
        if (!cancelled) {
          setIsAuthed(res.ok);
          if (res.ok) {
            const me = await res.json();
            setMeInfo(me);
          } else {
            setMeInfo(null);
          }
        }
      } catch (_) {
        if (!cancelled) setIsAuthed(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [route]);

  // Route guards based on auth state
  useEffect(() => {
    if (!authChecked) return;
    if (route === '/downloads' && !isAuthed) {
      navigate('/login');
    }
    if (route === '/login' && isAuthed) {
      navigate('/downloads');
    }
  }, [authChecked, isAuthed, route]);

  // Auto-open address modal after login when user has active order but incomplete address
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!authChecked || !isAuthed || !meInfo || addressPrompted) return;
      try {
        const sigRes = await fetch(`${API_BASE}/signature/status`, { credentials: 'include' });
        const sig = await sigRes.json().catch(()=>({}));
        const addrComplete = !!sig.addressComplete;
        const email = meInfo?.email;
        let hasActive = false;
        if (email) {
          const actRes = await fetch(`${API_BASE}/orders/has-active?email=${encodeURIComponent(email)}`);
          const data = await actRes.json().catch(()=>({ hasActive: false }));
          hasActive = !!data.hasActive;
        }
        if (!cancelled && hasActive && !addrComplete) {
          setShowAddressModal(true);
          setAddressPrompted(true);
        }
      } catch (_) {
        // ignore
      }
    };
    run();
    return () => { cancelled = true; };
  }, [authChecked, isAuthed, meInfo, addressPrompted]);

  return (
    <div className="App">
      <main>
        <h1>
          <span style={{ color: 'red', marginRight: '10px' }}>Σ</span>
          Barbotine Arbitrage
        </h1>
        <div className="header-links">
          <a href="/" onClick={(e)=>{e.preventDefault();navigate('/');}}>home</a>
          <a href="/downloads" onClick={(e)=>{e.preventDefault();navigate('/downloads');}}>downloads</a>
          {authChecked && !isAuthed && (
            <a href="/login" onClick={(e)=>{e.preventDefault();navigate('/login');}}>login</a>
          )}
          {authChecked && isAuthed && (
            <>
              <a href="/logout" onClick={(e)=>{e.preventDefault();navigate('/logout');}}>logout</a>
            </>
          )}
        </div>
        
        {isHome && (
        <div className="description">
          Scalable crypto arbitrage system for beginners, and more!
        </div>
        )}
        
        <div className="update-note">
          <strong>last update:</strong> August 26, 2025
        </div>
        
        {isHome && (
        <div className="button-container">
          <a 
            href="https://github.com/nelso0/barbotine-arbitrage-bot" 
            target='_blank'
            rel='noreferrer'
            className="btn btn-github"
            style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g transform="translate(-140.000000, -7559.000000)" fill="white">
                    <g transform="translate(56.000000, 160.000000)">
                      <path d="M94,7399 C99.523,7399 104,7403.59 104,7409.253 C104,7413.782 101.138,7417.624 97.167,7418.981 C96.66,7419.082 96.48,7418.762 96.48,7418.489 C96.48,7418.151 96.492,7417.047 96.492,7415.675 C96.492,7414.719 96.172,7414.095 95.813,7413.777 C98.04,7413.523 100.38,7412.656 100.38,7408.718 C100.38,7407.598 99.992,7406.684 99.35,7405.966 C99.454,7405.707 99.797,7404.664 99.252,7403.252 C99.252,7403.252 98.414,7402.977 96.505,7404.303 C95.706,7404.076 94.85,7403.962 94,7403.958 C93.15,7403.962 92.295,7404.076 91.497,7404.303 C89.586,7402.977 88.746,7403.252 88.746,7403.252 C88.203,7404.664 88.546,7405.707 88.649,7405.966 C88.01,7406.684 87.619,7407.598 87.619,7408.718 C87.619,7412.646 89.954,7413.526 92.175,7413.785 C91.889,7414.041 91.63,7414.493 91.54,7415.156 C90.97,7415.418 89.522,7415.871 88.63,7414.304 C88.63,7414.304 88.101,7413.319 87.097,7413.247 C87.097,7413.247 86.122,7413.234 87.029,7413.87 C87.029,7413.87 87.684,7414.185 88.139,7415.37 C88.139,7415.37 88.726,7417.2 91.508,7416.58 C91.513,7417.437 91.522,7418.245 91.522,7418.489 C91.522,7418.76 91.338,7419.077 90.839,7418.982 C86.865,7417.627 84,7413.783 84,7409.253 C84,7403.59 88.478,7399 94,7399" />
                    </g>
                  </g>
                </g>
              </svg> Github repo
          </a>
          <button 
            onClick={openModal}
            className="btn"
          >
            Support Barbotine
          </button>
        </div>
        )}

        {route === '/login' && <LoginPage />}
        {route === '/downloads' && isAuthed && <DownloadsPage />}
        {route === '/logout' && <LogoutPage onDone={()=>navigate('/')} />}
        {route === '/signature' && isAuthed && <SignaturePage />}
        {route === '/terms' && <TermsPage />}
        {route === '/refund' && <RefundPolicyPage />}
        {route === '/privacy' && <PrivacyPage />}

        {/* <div className="documentation-toggle-section">
          <button 
            onClick={toggleDocumentation}
            className="documentation-toggle-btn"
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="currentColor"
              className={`arrow-icon ${isDocumentationOpen ? 'rotated' : ''}`}
            >
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
            {isDocumentationOpen ? 'Hide full documentation' : 'Show full documentation'}
          </button>
          
          <div className={`documentation-container ${isDocumentationOpen ? 'open' : ''}`}>
            <Documentation />
          </div>
        </div> */}
        
        <div className="footer">
          <p>© 2025 barbotine | open source under creative commons license</p>
          <p>
            contact:{' '}
            nelso@barbotine.xyz
          </p>
          <p>
            <a style={{ color:'#666', textDecoration:'none', marginRight:'10px' }} href="/terms" onClick={(e)=>{e.preventDefault();navigate('/terms');}}>terms</a>
            <a style={{ color:'#666', textDecoration:'none', marginRight:'10px' }} href="/refund" onClick={(e)=>{e.preventDefault();navigate('/refund');}}>refund policy</a>
            <a style={{ color:'#666', textDecoration:'none' }} href="/privacy" onClick={(e)=>{e.preventDefault();navigate('/privacy');}}>privacy</a>
          </p>
          <p>
            <a href="https://github.com/nelso0">github</a> |{' '}
            <a href="https://youtube.com/@nelsorex">youtube</a> |{' '}
            <a href="https://twitter.com/@thebarbotine">twitter</a>
          </p>
        </div>
      </main>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button 
          onClick={scrollToTop}
          className="scroll-to-top"
          aria-label="Scroll to top"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
          </svg>
        </button>
      )}

      <SupportModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
      />
      <AddressModal 
        open={showAddressModal} 
        onClose={()=>setShowAddressModal(false)} 
        onSaved={()=>setShowAddressModal(false)} 
      />
    </div>
  );
}

export default App; 
